import { useEffect, useState } from "react";
import { Check, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  properties: "Properties & portfolio",
  condition_history: "Photo condition history",
  work_orders: "Work orders",
  scheduling: "Scheduling & calendars",
  inspections: "Inspections",
  issues: "Issue tracking",
  messaging: "Team messaging",
  owner_messaging: "Owner messaging",
  reports: "Reporting",
  exports: "Exports",
  invoicing: "Invoicing",
  payments: "Payment workflows",
  vendors: "Vendor management",
  automations: "Automations",
  weather: "NWS/NHC weather intelligence",
  bookings: "Airbnb & VRBO connectivity",
  owner_reports: "Owner reports",
  insights: "AI operational intelligence",
  maps: "Maps & routing",
  accounting: "Financial & accounting integrations",
  white_label: "White-label branding",
  api: "API access",
  integrations: "Advanced integrations",
  custom_workflows: "Custom workflows",
  enterprise_reporting: "Enterprise reporting",
  priority_support: "Priority support",
  dedicated_onboarding: "Dedicated onboarding",
  sso: "Custom security / SSO",
};

const WHY = [
  ["One platform, every plan",
   "No feature is locked behind a higher tier. Move up only when you need more properties or more people."],
  ["No surprise line items",
   "No per-user fees, no usage fees, no add-ons. Your bill is your plan plus any extra properties."],
  ["Know the price before you buy",
   "Extra properties are a published rate, so you can work out next month's invoice yourself."],
];

/** Shows the real subscription tiers from the platform. Signing up starts the existing
    free trial; plan checkout happens in-app under Settings, as it already does. */export default function PlanShowcase({ onStart, onBack }) {
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/plans`)
      .then((r) => r.json())
      .then((d) => setPlans((d.plans || []).filter((p) => p.value !== "trial")))
      .catch(() => setPlans([]));
  }, []);

  const priceLine = (p) => {
    if (p.contact_only) return { big: "Custom pricing", sub: `Starting at $${p.starting_price}+/month` };
    if (annual) {
      const save = p.price * 12 - p.annual_price;
      return { big: `$${p.annual_price.toLocaleString()}`, unit: "/year",
               sub: `$${Math.round(p.annual_price / 12)}/mo equivalent · save $${save.toLocaleString()} (${Math.round((save / (p.price * 12)) * 100)}%)` };
    }
    return { big: `$${p.price}`, unit: "/month", sub: `or $${p.annual_price.toLocaleString()}/year` };
  };

  return (
    <div data-testid="plan-showcase">
      <button type="button" data-testid="plans-back-btn" onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-gold
                   min-h-[40px] transition-colors duration-200">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-royal-deep mt-2">Pricing</h2>
      <p className="text-sm text-muted-foreground mt-2">
        You pay for the size of your operation — never for features. Every plan below is the same
        complete Cornerstone Ops platform.
      </p>

      <div className="mt-4 rounded-lg border border-gold/50 bg-gold/5 px-3.5 py-3"
           data-testid="everything-included-note">
        <p className="text-xs font-semibold text-royal-deep">Everything is included on every plan</p>
        <p className="text-[11px] text-muted-foreground mt-1">
          Plans differ by how many properties and users are included, and what an extra property
          costs. Nothing is locked behind a higher tier, and there are no per-user fees.
        </p>
      </div>

      <div className="mt-5 inline-flex rounded-full border border-border bg-secondary/60 p-1"
           data-testid="billing-toggle">
        {[["Monthly", false], ["Annual · save 16%", true]].map(([label, val]) => (
          <button key={label} type="button" onClick={() => setAnnual(val)}
            data-testid={val ? "billing-annual" : "billing-monthly"}
            aria-pressed={annual === val}
            className={`rounded-full px-3.5 min-h-[38px] text-xs font-semibold transition-colors duration-200
                        ${annual === val ? "bg-royal text-white" : "text-royal-deep hover:text-royal"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {plans.map((p) => {
          const line = priceLine(p);
          return (
            <div key={p.value} data-testid={`plan-${p.value}`}
                 className={`relative rounded-lg border p-4 transition-shadow duration-200
                             ${p.contact_only ? "border-gold/50 bg-royal-deep"
                               : p.popular ? "bg-white border-gold shadow-[0_0_0_1px_hsl(var(--gold))]"
                               : "bg-white border-border"}`}>
              {p.popular && (
                <span data-testid="most-popular-badge"
                      className="absolute -top-2.5 left-4 rounded-full bg-gold px-2.5 py-0.5 text-[10px]
                                 font-bold uppercase tracking-[0.16em] text-royal-deep">
                  Most Popular
                </span>
              )}
              <p data-testid={`plan-scale-${p.value}`}
                 className={`text-[10px] font-bold uppercase tracking-[0.16em]
                             ${p.contact_only ? "text-gold/70" : "text-muted-foreground"}`}>
                {p.scale}
              </p>
              <div className="flex items-baseline justify-between gap-3 mt-1">
                <p className={`font-display text-base font-semibold ${p.contact_only ? "text-gold" : "text-royal-deep"}`}>
                  {p.label}
                </p>
                <p className={`font-display font-bold ${p.contact_only ? "text-base text-gold" : "text-xl text-royal-deep"}`}
                   data-testid={`plan-price-${p.value}`}>
                  {line.big}
                  {line.unit && <span className="text-xs font-medium text-muted-foreground">{line.unit}</span>}
                </p>
              </div>
              <p className={`text-[11px] mt-0.5 text-right ${p.contact_only ? "text-gold" : "text-muted-foreground"}`}>
                {line.sub}
              </p>
              <p className={`text-xs mt-2 ${p.contact_only ? "text-gold/90" : "text-muted-foreground"}`}>
                {p.blurb}
              </p>
              <p data-testid={`plan-includes-${p.value}`}
                 className={`text-[11px] mt-2 font-medium ${p.contact_only ? "text-gold/80" : "text-royal-deep"}`}>
                {p.max_properties === null ? "Unlimited" : p.max_properties} properties ·
                {" "}{p.max_users === null ? "unlimited" : p.max_users} users
                {p.overage_rate ? ` · extra properties $${p.overage_rate}/property/month` : ""}
              </p>
              {!p.contact_only && (
                <ul className="mt-2.5 grid gap-1 sm:grid-cols-2">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-center gap-1.5 text-[11px] text-royal-deep">
                      <Check className="h-3 w-3 shrink-0 text-royal" /> {LABELS[f] || f}
                    </li>
                  ))}
                </ul>
              )}
              {p.contact_only && (
                <>
                  <p className="mt-2.5 text-[11px] text-gold/90">
                    Everything in Business, plus custom deployment, integrations, onboarding and support.
                  </p>
                  <p className="mt-3 text-[11px] text-gold font-semibold">
                    Arranged with our team — no automatic billing.
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* The closing argument: most tools charge more for better software. We only charge for size. */}
      <section className="mt-7 rounded-lg border border-border bg-white p-5"
               data-testid="why-pricing-simple">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          Why our pricing is simple
        </p>
        <h3 className="font-display text-lg font-bold text-royal-deep mt-1.5">
          You pay for the size of your operation. Never for features.
        </h3>
        <p className="text-xs text-muted-foreground mt-2">
          Most platforms hide the tools you actually need behind the plan above the one you picked.
          We don't. Starter, Professional and Business are the same complete Cornerstone Ops — same
          weather intelligence, same automations, same inspections, same reporting.
        </p>
        <dl className="mt-4 grid gap-2.5">
          {WHY.map(([term, detail]) => (
            <div key={term} className="rounded-md border border-border bg-secondary/40 px-3.5 py-3">
              <dt className="font-display text-xs font-bold text-royal-deep">{term}</dt>
              <dd className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{detail}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs font-semibold text-royal-deep mt-4">
          More properties, higher price. More users, higher plan. Better software? Already included.
        </p>
      </section>

      <button data-testid="plans-start-btn" onClick={onStart}
        className="mt-5 h-12 w-full rounded-md bg-gold font-display text-sm font-bold uppercase
                   tracking-[0.14em] text-royal-deep transition-transform duration-200 hover:scale-[1.01]">
        Start With Cornerstone
      </button>
      <p className="mt-2.5 text-[11px] text-muted-foreground text-center">
        14-day free trial · card payment processing fees are billed separately from your subscription.
      </p>
    </div>
  );
}
